export type ApplicationInfo = {
  serviceName: string;
  displayName: string;
  status: 'string';
  callPerHour: number;
  durationAvg: string;
  loginSuccess?: number;
  loginError?: number;
  graphs?: [
    {
      name: string;
      value: string[][];
    }
  ];
};

export type TransactionErrorInfo = {
  operationName: string;
  count: number;
  error: string;
  sourceType: string;
  responseTime: string;
};

export type TransactionC2MInfo = {
  operationName: string;
  count: number;
  error: string;
  success: string;
  errorMessage?: string;
  errorMessageCount?: string;
};

export type TransactionInfo = {
  operationName: string;
  count: number;
  successCount: number;
  errorCount: number;
  responseTime: string;
};

export type ApiError = {
  errorMessage: string;
};

export type ApiResponse<T> = {
  result: T | ApiError;
  lastUpdated: string;
  /** Backend that produced this response during the Elastic → AWS migration. */
  dataSource?: 'elastic' | 'aws';
};

export type ssmInfo = {
  serviceName: string;
  successCount: number;
  errorCount?: number;
  linkToDashboard?: string;
  responseTime?: string;
  graphs?: [
    {
      name: string;
      value: string[][];
    }
  ];
};

export type AlertInfo = {
  AlertId: number;
  AlertName: string;
  widgetTitle: string;
  preference: boolean;
  threshold: string;
};
export type AlertBody = Pick<AlertInfo, 'AlertId' | 'preference'>;
export type PaymentInfo = {
  paymentDetailInfo: [
    {
      channel: string;
      totalPayment: number;
      error: number;
    }
  ];
  total_rejected: number;
  total_success: number;
  linkToDashboard: string;
  paymentProvider: string;
  graph?: [
    {
      name: string;
      value: (string | number)[][];
    }
  ];
};
export type ReportHtmlInfo = {
  html: string;
};

export type ReportFilters = {
  env?: string;
  delivery_type?: string;
  notification_type?: string;
  mode?: string;
  start_date?: string;
  end_date?: string;
};
export enum ApplicationStatus {
  live = 'live',
  paused = 'paused',
  unknown = 'unknown',
  moderate = 'moderate'
}
export type ApplicationStatusInfo = {
  service_name: string;
  status: ApplicationStatus;
};

export type PageUsageInfo = {
  path: string;
  hits: string;
};

export type AuthSessionsInfo = {
  failed: string; // unauthenticated (loggedOut)
  success: string; // authenticated (loggedIn)
};

export type ApiResponseTime = {
  key: string;
  doc_count: string;
};

export type WebOutageInfo = {
  outage_count: string;
  customer_affected: string;
  lastUpdated: string;
  outage_total_user: string;
};

export type OutageScreenshotSummary = {
  checkGroup: string;
  timestamp?: string;
  thumbnail: string;
};

export type OutageScreenshotDetail = {
  checkGroup: string;
  image: string;
};

// ─── App Runtime Config toggles ───────────────────────────────────────────────

export type AppToggleInfo = {
  key: string;
  name: string;
  description?: string;
  enabled: boolean;
  deprecated: boolean;
};

/** Rollout state of the latest AppConfig deployment. Values mirror the backend enum. */
export type AppToggleDeploymentState =
  | 'None'
  | 'InProgress'
  | 'Complete'
  | 'Stopped';

export type AppTogglesInfo = {
  toggles: AppToggleInfo[];
  /** AppConfig hosted configuration version, echoed back on write for optimistic locking. */
  versionNumber: number;
  deploymentState: AppToggleDeploymentState;
  deploymentNumber?: string;
  deployedVersion?: number;
  /** True while the backend is serving the in-memory stub rather than real AppConfig. */
  isStub: boolean;
};

export type AppToggleChange = {
  key: string;
  enabled: boolean;
};

/**
 * Why a toggle write was refused. The backend distinguishes these because the fix
 * differs: reload for a conflict, wait for a rollout, and 'notAuthorized' means the
 * server-side gate rejected the caller.
 */
export type AppToggleWriteFailure =
  | 'notAuthorized'
  | 'versionConflict'
  | 'deploymentInProgress'
  | 'unknown';

export enum TransactionTypes {
  VOLUME = 'volume',
  ERROR = 'error',
  C2M_VOLUME = 'c2m_volume',
  API_RESPONSE_TIME = 'api_response_time'
}

// ─── Non-Prod Security ────────────────────────────────────────────────────────

export type WafManagedRuleGroup = {
  vendor: string;
  name: string;
};

export type WafRuleInfo = {
  name: string;
  priority: number;
  action: string;
  type: string;
  managedRuleGroup?: WafManagedRuleGroup;
  ipRestrictions?: string[];
  geoRestrictions?: string[];
  rateLimit?: number;
};

export type WafAclInfo = {
  id: string;
  name: string;
  rules: WafRuleInfo[];
};

export type CloudFrontSecurityInfo = {
  id: string;
  domainName: string;
  aliases: string[];
  status: string;
  enabled: boolean;
  isPubliclyAccessible: boolean;
  geoRestrictionType: string;
  geoRestrictedLocations: string[];
  webAclId: string;
  waf?: WafAclInfo;
};

export type AmplifySecurityInfo = {
  id: string;
  name: string;
  defaultDomain: string;
  customDomains: string[];
  status: string;
  platform: string;
  basicAuthEnabled: boolean;
  waf?: WafAclInfo;
};

export type EnvironmentSecurityInfo = {
  name: string;
  distributions: CloudFrontSecurityInfo[];
  amplifyApps: AmplifySecurityInfo[];
};

export type NonProdSecurityInfo = {
  environments: EnvironmentSecurityInfo[];
};

// ─── Diagnostic Metrics ───────────────────────────────────────────────────────

export type DeviceErrorMetric = {
  device: string;
  sessionCount: string;
  errorSessions: string;
  errorRate: string;
  errorRatePercent: string;
};

export type DeviceHealthInfo = DeviceErrorMetric[];

export type ErrorBreakdownMetric = {
  errorType: string;
  count: number;
  pctOfSessions: string;
  totalSessions: number;
  isTotal: boolean;
};

export type ErrorBreakdownInfo = ErrorBreakdownMetric[];

export type EventErrorMetric = {
  eventType: string;
  totalCount: number;
  byDevice: Array<{
    device: string;
    count: number;
  }>;
};

export type EventDiagnosticsInfo = EventErrorMetric[];

export type LatencyBucketMetric = {
  rttBucket: string;
  sessionCount: string;
  errorSessions: string;
  errorRate: string;
  errorRatePercent: string;
  avgRttMs: string;
};

export type PageErrorMetric = {
  page: string;
  sessionCount: string;
  errorSessions: string;
  errorRate: string;
  errorRatePercent: string;
  avgLcpMs?: string;
};

export type ConversionFunnelMetric = {
  device: string;
  osVersion: string;
  engagedSessions: string;
  conversionSessions: string;
  successRate: string;
  successRatePercent: string;
  cartAbandonmentRate: string;
  cartAbandonmentRatePercent: string;
};

export type PerformanceTrendInfo =
  | LatencyBucketMetric[]
  | PageErrorMetric[]
  | ConversionFunnelMetric[];

// ─── Temporal ─────────────────────────────────────────────────────────────────

export type TemporalFailedActivity = {
  workflowId: string;
  latestTimestamp: string;
  failingActivity: string;
  lastAttempt: string;
};

export type TemporalLogGroupResult = {
  log: string;
  title: string;
  output: TemporalFailedActivity[];
};

export type TemporalInfo = TemporalLogGroupResult[];

// ─── Synthetic Canaries ────────────────────────────────────────────────────────

export type SyntheticCanaryHealth = 'PASSED' | 'FAILED' | 'FLAKY' | 'UNKNOWN';

export type SyntheticCanaryInfo = {
  name: string;
  state: string; // RUNNING | STOPPED | ERROR | CREATING | UPDATING | DELETING | READY
  lastRunStatus: string; // PASSED | FAILED | RUNNING | UNKNOWN
  lastRunTimestamp?: string;
  successPercent?: number;
  avgDurationMs?: number;
  runsEvaluated: number;
  health: SyntheticCanaryHealth;
};

export type SyntheticCanariesInfo = {
  canaries: SyntheticCanaryInfo[];
};
